import React, { useState } from 'react';
import { Mail, Lock, BookOpen, Star, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Input, Card } from '../ui';
import { getAuthErrorMessage } from '../../utils/errorHandler';
import { useNavigate } from 'react-router-dom';

import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

interface LoginFormProps {}

const LoginForm: React.FC<LoginFormProps> = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Mark that this is a Standard Login attempt
        sessionStorage.setItem('superAdminGateway', 'false');
        sessionStorage.removeItem('authError');

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const firebaseUser = userCredential.user;

            // Check if onAuthStateChanged signed us out in the background
            const backgroundError = sessionStorage.getItem('authError');
            if (backgroundError) {
                setError(backgroundError);
                sessionStorage.removeItem('authError');
                setLoading(false);
                return;
            }

            // Fetch firestore document
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            let role = '';
            if (userDoc.exists()) {
                role = userDoc.data().role;
            }

            if (role === 'super_admin') {
                await auth.signOut();
                setError('Access Denied: Please use the Super Admin Gateway.');
                setLoading(false);
                return;
            }

            const attemptedLocation = sessionStorage.getItem('attemptedLocation');
            if (attemptedLocation && attemptedLocation !== '/login') {
                navigate(attemptedLocation, { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        } catch (error: any) {
            setError(getAuthErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full max-w-md mx-auto">
            {/* Islamic School Header with Auto-Fallback Logo */}
            <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                    <div className="relative">
                        <img 
                            src="/logo.png" 
                            alt="Noor-e-Hamza Logo" 
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                            className="w-20 h-20 object-contain mx-auto"
                        />
                        <div className="hidden">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg animate-glow mx-auto">
                                <BookOpen className="h-8 w-8 text-white" />
                            </div>
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center animate-float">
                                <Star className="h-3 w-3 text-white" />
                            </div>
                        </div>
                    </div>
                </div>
                <h2 className="text-4xl font-bold gradient-text mb-2">Noor-e-Hamza</h2>
                <h2 className="text-2xl font-bold gradient-text mb-2">Islamic College</h2>
                <p className="text-gray-600 font-medium">Knowledge • Wisdom • Excellence</p>
                <div className="mt-2 text-sm text-emerald-600 font-medium">Sign in to your account</div>
            </div>

            <Card glassy={true} className="islamic-pattern">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="bg-red-50/80 backdrop-blur-sm border border-red-200 text-red-600 px-4 py-3 rounded-xl shadow-sm">
                            {error}
                        </div>
                    )}

                    <Input
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        icon={<Mail className="h-5 w-5" />}
                        required
                    />

                    <Input
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        icon={<Lock className="h-5 w-5" />}
                        showPasswordToggle={true}
                        required
                    />

                    <Button
                        type="submit"
                        loading={loading}
                        fullWidth
                        size="lg"
                        className="mt-8"
                    >
                        {loading ? 'Signing In...' : 'Sign In'}
                    </Button>
                </form>
            </Card>
        </div>
    );
};

export default LoginForm;
